import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useSidebar } from '@/contexts/SidebarContext';
import { LayoutDashboard, FileText, Settings, Users, BarChart3, Database, Code, BookOpen, Shield, Inbox, Coins, ClipboardList } from 'lucide-react';
import { useUserStore } from '@ekonavi/auth';

const menuItems = [
	{
		title: 'Dashboard',
		href: '/dashboard',
		icon: LayoutDashboard,
	},
	{
		title: 'Forms Manager',
		href: '/forms',
		icon: FileText,
	},
	{
		title: 'Reports',
		href: '/reports',
		icon: ClipboardList,
	},
	{
		title: 'Users',
		href: '/users',
		icon: Users,
	},
	{
		title: 'Roles',
		href: '/roles',
		icon: Shield,
	},
	{
		title: 'Role Requests',
		href: '/requests',
		icon: Inbox,
	},
];

const resourceMenuItems = [
	{
		title: 'Blockchains',
		href: '/blockchains',
		icon: Database,
	},
	{
		title: 'Smart Contracts',
		href: '/contracts',
		icon: Code,
	},
	{
		title: 'Methodologies',
		href: '/methodologies',
		icon: BookOpen,
	},
	{
		title: 'Tokens',
		href: '/tokens',
		icon: Coins,
	},
];

const systemMenuItems = [
	{
		title: 'Analytics',
		href: '/analytics',
		icon: BarChart3,
	},
	{
		title: 'Settings',
		href: '/settings',
		icon: Settings,
	},
];

const Sidebar = () => {
	const location = useLocation();
	const { isSidebarOpen, closeSidebar, isMobile } = useSidebar();
	const { user } = useUserStore();

	const handleLinkClick = () => {
		if (isMobile) {
			closeSidebar();
		}
	};

	if (!isSidebarOpen) return null;

	return (
		<>
			{/* Mobile backdrop */}
			{isMobile && (
				<div
					className='fixed inset-0 bg-black/50 z-40 md:hidden'
					onClick={closeSidebar}
				/>
			)}

			{/* Sidebar */}
			<div className={cn('bg-card border-r border-border flex flex-col z-[99]', isMobile ? 'fixed inset-y-0 left-0 w-64 shadow-lg' : 'w-64 relative')}>
				<div className='p-6 border-b border-border'>
					<h1 className='text-xl font-semibold text-foreground'>Admin Panel</h1>
					<p className='text-sm text-muted-foreground mt-1'>Faz Ekonavi Admin</p>
				</div>

				<nav className='flex-1 p-4 space-y-4 overflow-y-auto'>
					{/* Main Menu */}
					<div className="space-y-2">
						{menuItems.map((item) => {
							const Icon = item.icon;
							const isActive = location.pathname === item.href;

							return (
								<Link
									key={item.href}
									to={item.href}
									onClick={handleLinkClick}
									className={cn('flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors', isActive ? 'bg-primary text-primary-foreground font-semibold' : 'text-muted-foreground hover:text-foreground hover:bg-accent')}>
									<Icon size={18} />
									{item.title}
								</Link>
							);
						})}
					</div>

					{/* Divider */}
					<div className="border-t border-border" />

					{/* Resource Management Section */}
					<div className="space-y-2">
						<div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
							Resources
						</div>
						{resourceMenuItems.map((item) => {
							const Icon = item.icon;
							const isActive = location.pathname === item.href;

							return (
								<Link
									key={item.href}
									to={item.href}
									onClick={handleLinkClick}
									className={cn('flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors', isActive ? 'bg-primary text-primary-foreground font-semibold' : 'text-muted-foreground hover:text-foreground hover:bg-accent')}>
									<Icon size={18} />
									{item.title}
								</Link>
							);
						})}
					</div>

					{/* Divider */}
					<div className="border-t border-border" />

					{/* System Section */}
					<div className="space-y-2">
						<div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
							System
						</div>
						{systemMenuItems.map((item) => {
							const Icon = item.icon;
							const isActive = location.pathname === item.href;

							return (
								<Link
									key={item.href}
									to={item.href}
									onClick={handleLinkClick}
									className={cn('flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors', isActive ? 'bg-primary text-primary-foreground font-semibold' : 'text-muted-foreground hover:text-foreground hover:bg-accent')}>
									<Icon size={18} />
									{item.title}
								</Link>
							);
						})}
					</div>
				</nav>

				<div className='p-4 border-t border-border'>
					<div className='flex items-center gap-3 px-3 py-2'>
						<div className='w-8 h-8 bg-primary rounded-full flex items-center justify-center'>
							<span className='text-primary-foreground text-sm font-medium'>
								{user.username ? user.username.split(' ').map((n: string) => n[0]).join('').toUpperCase() : 'U'}
							</span>
						</div>
						<div className='flex-1 min-w-0'>
							<p className='text-sm font-medium text-foreground truncate'>
								{user.username || 'User'}
							</p>
							<p className='text-xs text-muted-foreground truncate'>
								{user.email || 'user@example.com'}
							</p>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default Sidebar;